import React, { useEffect, useRef, useState } from "react";
import cx from "classnames";
import { animated, useSpring } from "@react-spring/web";
import useMeasure from "react-use-measure";
import styles from "./common.module.scss";

export type LeadsReportsTabKey = "reports" | "leads";

interface LeadsReportsTabsProps {
  active: LeadsReportsTabKey;
  // eslint-disable-next-line no-unused-vars
  onChange: (tab: LeadsReportsTabKey) => void;
}

const TABS: { key: LeadsReportsTabKey; label: string }[] = [
  { key: "reports", label: "Reports" },
  { key: "leads", label: "Leads" },
];

export const LeadsReportsTabs: React.FC<LeadsReportsTabsProps> = ({ active, onChange }) => {
  const [boundsRef, bounds] = useMeasure();
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const [indicatorStyle, setIndicatorStyle] = useState({ left: 0, width: 0 });
  const hasMeasured = indicatorStyle.width > 0;

  const springProps = useSpring({
    to: hasMeasured
      ? {
          left: indicatorStyle.left,
          width: indicatorStyle.width,
        }
      : undefined,
    config: {
      tension: 300,
      friction: 30,
    },
  });

  useEffect(() => {
    const selectedIndex = TABS.findIndex((tab) => tab.key === active);
    const selectedTabEl = tabRefs.current[selectedIndex];

    if (selectedTabEl) {
      const tabBounds = selectedTabEl.getBoundingClientRect();
      const containerBounds = selectedTabEl.parentElement?.getBoundingClientRect();

      if (containerBounds) {
        setIndicatorStyle({
          left: tabBounds.left - containerBounds.left,
          width: tabBounds.width,
        });
      }
    }
  }, [active, bounds]);

  return (
    <div ref={boundsRef} className={styles.tabs}>
      <animated.div className={styles.tabIndicator} style={springProps} />

      {TABS.map((tab, index) => (
        <button
          key={tab.key}
          ref={(el) => {
            tabRefs.current[index] = el;
          }}
          className={cx(styles.tab, { [styles.active]: tab.key === active })}
          onClick={() => onChange(tab.key)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
};
